// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { create } from 'zustand'
import type { OnboardingData } from '@/types'

const TOTAL_STEPS = 9

interface OnboardingState {
  currentStep:    number
  totalSteps:     number
  data:           Partial<OnboardingData>
  isSubmitting:   boolean
  isComplete:     boolean

  // Actions
  setStep:        (step: number) => void
  nextStep:       () => void
  prevStep:       () => void
  updateData:     (updates: Partial<OnboardingData>) => void
  setSubmitting:  (submitting: boolean) => void
  setComplete:    () => void
  reset:          () => void
}

const initialData: Partial<OnboardingData> = {
  firstName:        '',
  lastName:         '',
  dateOfBirth:      '',
  email:            '',
  phone:            '',
  street:              '',
  houseNumber:         '',
  houseNumberAddition: '',
  city:                '',
  postalCode:          '',
  maritalStatus:    '',
  hasChildren:      false,
  numberOfChildren: 0,
  password:         '',
  passwordConfirm:  '',
  privacyAccepted:  false,
  termsAccepted:    false,
}

export const useOnboardingStore = create<OnboardingState>((set) => ({
  currentStep:   1,
  totalSteps:    TOTAL_STEPS,
  data:          { ...initialData },
  isSubmitting:  false,
  isComplete:    false,

  setStep: (step) =>
    set({ currentStep: Math.max(1, Math.min(step, TOTAL_STEPS)) }),

  nextStep: () =>
    set((state) => ({
      currentStep: Math.min(state.currentStep + 1, TOTAL_STEPS),
    })),

  prevStep: () =>
    set((state) => ({
      currentStep: Math.max(state.currentStep - 1, 1),
    })),

  updateData: (updates) =>
    set((state) => ({
      data: { ...state.data, ...updates },
    })),

  setSubmitting: (submitting) =>
    set({ isSubmitting: submitting }),

  setComplete: () =>
    set({ isComplete: true }),

  reset: () =>
    set({
      currentStep:  1,
      data:         { ...initialData },
      isSubmitting: false,
      isComplete:   false,
    }),
}))
